// A sauna's woods in the 3D view (docs/claude/woods.md): each wood's image as a
// texture, loaded once, and the room's meshes dressed in them — the walls split
// into their inside and outside faces, the flat ceiling into its underside and
// the roof, every face given the UVs that lay its boards the way they run.

import * as THREE from 'three';
import type {
  ProductInstance,
  Vec2,
} from '../../shared/types';
import {
  ceilingSlatsOf,
  wallSlatsOf,
  type WoodId,
  woodImageMm,
  woodOf,
  WOODS,
  woodUrl,
} from '../../shared/woods';
import {
  boardUVs,
  faceSide,
  imageUV,
  ownImageUVs,
  type P3,
  surfaceMm,
  type WoodImg,
} from '../woodFaces';
import { withLook } from './sceneNodes';
import { grilleWood } from '../floorGrille';

export interface Image3d { tex: THREE.Texture | null; ready: boolean; waiting: Array<() => void> }
const images = new Map<string, Image3d>();
const listeners = new Set<() => void>();

/** Calls `fn` each time a texture's image (a wood's, a finish's) has loaded, so
 *  the view draws again. */
export function onTextureLoaded(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Tell the views a texture has arrived (an image loaded, a finish's made). */
export function announceTexture(): void {
  for (const f of listeners) f();
}

/** A texture's image (`woodUrl`), asked for once: plain same-origin
 *  (no CORS request, which a file:// page would fail), and none at all in node
 *  (the GLB export). */
export function imageOf(url: string): Image3d {
  let e = images.get(url);
  if (e) return e;
  const entry: Image3d = e = { tex: null, ready: false, waiting: [] };
  images.set(url, entry);
  if (typeof document === 'undefined') return entry;
  const img = document.createElement('img');
  img.onload = () => {
    const t = new THREE.Texture(img);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    t.needsUpdate = true;
    entry.tex = t;
    entry.ready = true;
    for (const f of entry.waiting.splice(0)) f();
    announceTexture();
  };
  img.onerror = () => { entry.waiting.length = 0; };
  img.src = url;
  return entry;
}

/** A material clad in a wood, darkened by `shade` (a bench's frame): its image
 *  once loaded, its average colour until then, and where none can load. */
export function woodMaterial(id: WoodId, shade = 1): THREE.MeshStandardMaterial {
  const avg = new THREE.Color(WOODS[id].color).multiplyScalar(shade);
  // Exported in its average colour: three's own is the white its image is multiplied by.
  const m = withLook(new THREE.MeshStandardMaterial({ color: avg.clone(), roughness: 0.6, metalness: 0, side: THREE.DoubleSide }), 'solid', avg);
  const e = imageOf(woodUrl(id));
  const clad = (): void => { m.map = e.tex; m.color.setScalar(shade); m.needsUpdate = true; };
  if (e.ready) clad(); else e.waiting.push(clad);
  return m;
}

/** A wood's image on a surface. */
function imgOf(id: WoodId): WoodImg {
  return { ...woodImageMm(WOODS[id]), boards: WOODS[id].boards };
}

/** One surface of a room: a material, and how its image lies — its boards'
 *  way (null: the colour's, whose mesh keeps the UVs it was built with), or a
 *  triangle's own UVs (`uvs`, from its points and normal). */
export interface Surface { mat: THREE.Material; img: WoodImg | null; turn: boolean; uvs?: (tri: readonly P3[], n: P3) => number[] }

/** What a room's walls, ceiling and roof are BUILT in while it is clad (in woods,
 *  or a steam room in its finishes): stand-ins that `dress` replaces, never drawn. */
/** What a mesh handed to `dress` is (the build knows; the mesh does not say). */
export interface DressAt { ceiling: boolean; ownUV: boolean }

export interface Cladding {
  wall: THREE.Material;
  ceil: THREE.Material;
  roof: THREE.Material;
  /** A bench's material in its wood (`structure`: its frame, a shade darker), or
   *  null when the benches keep their colours. */
  bench: ((structure: boolean) => THREE.Material) | null;
  /** The floor grille's, as a bench's (`structure`: its battens), or null when
   *  it keeps its colour (floorGrille.ts `grilleWood`). */
  grille: ((structure: boolean) => THREE.Material) | null;
  /** A mesh built for the room, as the meshes it is drawn as: `ceiling` — it is
   *  the room's ceiling (a flat slab: its underside the ceiling, the rest the
   *  roof); `ownUV` — its UVs are already true surface mm (a shaped shell's, a
   *  sculpted bench's), kept instead of laid out again. */
  dress(m: THREE.Mesh, at: DressAt): THREE.Mesh[];
}

/**
 * The room's cladding, or null when it has no wood (its colours' timber, built
 * as it always was). `colour` holds the materials of its colours, for a finish
 * with no wood; `axes` are its longest wall's direction and inward normal, which
 * a level face's boards run along (as a shaped ceiling's do).
 */
export function roomCladding(
  inst: ProductInstance,
  colour: { wall: THREE.Material; ceil: THREE.Material },
  outer: readonly Vec2[],
  axes: { u: Vec2; n: Vec2 },
): Cladding | null {
  const w = inst.woods;
  const wIn = woodOf(w?.wallsIn?.wood), wOut = woodOf(w?.wallsOut?.wood) ?? wIn;
  const wCeil = woodOf(w?.ceiling?.wood) ?? wIn, wBench = woodOf(w?.benches?.wood);
  const wGrille = inst.floorGrille ? grilleWood(inst) : null;
  if (!wIn && !wOut && !wCeil && !wBench && !wGrille) return null;
  // The images' boards run along their x: a vertical wall turns them, as does
  // a ceiling's laid across its longest wall.
  const inside: Surface = wIn ? { mat: woodMaterial(wIn.id), img: imgOf(wIn.id), turn: wallSlatsOf(w, 'in') === 'vertical' } : { mat: colour.wall, img: null, turn: false };
  const outside: Surface = !wOut ? inside
    : { mat: wOut.id === wIn?.id ? inside.mat : woodMaterial(wOut.id), img: imgOf(wOut.id), turn: wallSlatsOf(w, 'out') === 'vertical' };
  const ceiling: Surface = wCeil
    ? { mat: woodMaterial(wCeil.id), img: imgOf(wCeil.id), turn: ceilingSlatsOf(w) === 'crosswise' }
    : { mat: colour.ceil, img: null, turn: false };
  // The roof (the slab's top and edges, a shaped shell's outer surface and
  // fascia) is the outside's once the walls have a wood: the ceiling's else.
  const roof = wIn || wOut ? outside : ceiling;
  const stand = (name: string): THREE.Material => { const m = new THREE.MeshBasicMaterial(); m.name = name; return m; };
  const wall = stand('wall'), ceil = stand('ceiling'), roofIn = stand('roof');
  let bench: Cladding['bench'] = null;
  const boardMats = new Map<THREE.Material, WoodImg>();
  if (wBench) {
    const board = woodMaterial(wBench.id), frame = woodMaterial(wBench.id, 0.72), img = imgOf(wBench.id);
    boardMats.set(board, img).set(frame, img);
    bench = (structure) => (structure ? frame : board);
  }
  let grille: Cladding['grille'] = null;
  if (wGrille) {
    const same = wGrille.id === wBench?.id && bench;
    const board = same ? bench!(false) : woodMaterial(wGrille.id), frame = same ? bench!(true) : woodMaterial(wGrille.id, 0.72), img = imgOf(wGrille.id);
    boardMats.set(board, img).set(frame, img);
    grille = (structure) => (structure ? frame : board);
  }
  let boardN = 0;
  const dress = (m: THREE.Mesh, at: DressAt): THREE.Mesh[] => {
    const mat = m.material as THREE.Material;
    if (mat === wall) return split(m, inst, (c, n) => (faceSide(outer, c, n) === 'out' ? outside : inside), axes, false);
    if (mat === roofIn) return split(m, inst, () => roof, axes, at.ownUV);
    if (mat === ceil) {
      // The flat slab: its underside is the ceiling, the rest of it the roof.
      if (!at.ownUV && at.ceiling) return split(m, inst, (_c, n) => (n[2] < -0.7 ? ceiling : roof), axes, false);
      return split(m, inst, () => ceiling, axes, at.ownUV);
    }
    const img = boardMats.get(mat);
    if (img) {
      const g = localGeometry(m, inst);
      const uv = at.ownUV && g.uv ? ownImageUVs(g.uv, img) : boardUVs(g.pts, g.nrm, img, boardN++ * 7);
      m.geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    }
    return [m];
  };
  return { wall, ceil, roof: roofIn, bench, grille, dress };
}

/** A room mesh's points in the room's LOCAL frame, with the normal of the face
 *  each is on, and its UVs. Room meshes are built unindexed. */
export function localGeometry(m: THREE.Mesh, inst: ProductInstance): { pts: P3[]; nrm: P3[]; uv: ArrayLike<number> | null } {
  const geo = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry;
  if (geo !== m.geometry) m.geometry = geo;
  if (!geo.getAttribute('normal')) geo.computeVertexNormals();
  const pos = geo.getAttribute('position'), nor = geo.getAttribute('normal');
  const r = (-inst.rotation * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  const pts: P3[] = [], nrm: P3[] = [];
  for (let i = 0; i < pos.count; i++) {
    // three's frame is (plan x, height, −plan y).
    const dx = pos.getX(i) - inst.x, dy = -pos.getZ(i) - inst.y;
    pts.push([c * dx - s * dy, s * dx + c * dy, pos.getY(i)]);
    const nx = nor.getX(i), ny = -nor.getZ(i);
    nrm.push([c * nx - s * ny, s * nx + c * ny, nor.getY(i)]);
  }
  return { pts, nrm, uv: (geo.getAttribute('uv')?.array as ArrayLike<number> | undefined) ?? null };
}

/** A mesh as one mesh per surface its faces take (`surfaceOf`, read at each
 *  triangle's centre and normal), each in its surface's material, with UVs that
 *  lay its image — a triangle's own (`uvs`), else from the mesh's own when `own`
 *  (true millimetres), else along each face (`surfaceMm`). A surface with no
 *  image keeps the mesh's UVs. The pieces are one solid, which the build records
 *  (sceneNodes.ts) and a 3D section fills where it cuts it. */
export function split(m: THREE.Mesh, inst: ProductInstance, surfaceOf: (c: P3, n: P3) => Surface, axes: { u: Vec2; n: Vec2 }, own: boolean): THREE.Mesh[] {
  const g = localGeometry(m, inst);
  const pos = m.geometry.getAttribute('position'), nor = m.geometry.getAttribute('normal');
  const parts = new Map<Surface, number[]>();
  for (let t = 0; t + 2 < g.pts.length; t += 3) {
    const a = g.pts[t], b = g.pts[t + 1], c = g.pts[t + 2];
    const f = surfaceOf([(a[0] + b[0] + c[0]) / 3, (a[1] + b[1] + c[1]) / 3, (a[2] + b[2] + c[2]) / 3], g.nrm[t]);
    let list = parts.get(f);
    if (!list) parts.set(f, list = []);
    list.push(t);
  }
  const out: THREE.Mesh[] = [];
  for (const [f, tris] of parts) {
    const P: number[] = [], N: number[] = [], U: number[] = [];
    for (const t of tris) {
      if (f.uvs) U.push(...f.uvs([g.pts[t], g.pts[t + 1], g.pts[t + 2]], g.nrm[t]));
      for (let k = t; k < t + 3; k++) {
        P.push(pos.getX(k), pos.getY(k), pos.getZ(k));
        N.push(nor.getX(k), nor.getY(k), nor.getZ(k));
        if (f.uvs) continue;
        if (!f.img) U.push(g.uv?.[2 * k] ?? 0, g.uv?.[2 * k + 1] ?? 0);
        else {
          const mm: [number, number] = own && g.uv ? [g.uv[2 * k], g.uv[2 * k + 1]] : surfaceMm(g.pts[k], g.nrm[k], axes);
          U.push(...imageUV(mm, f.img, f.turn));
        }
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(N, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(U, 2));
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    out.push(new THREE.Mesh(geo, f.mat));
  }
  m.geometry.dispose();
  return out;
}
